import { HoverCard } from '@mantine/core';
import {
  AVAILABILITY_SLOTS,
  AVAILABILITY_VALUE_LABEL,
  formatDuration,
  isExternal,
  type AvailabilityValue,
} from '@reditelji/shared';
import type { EventDay, PoolVolunteer, Shift, TimelinePayload } from '../api/types';
import { useUi } from '../state/ui';
import type { VolunteerShift } from '../volunteers/derive';
import { groupShiftsByDay, initials, range, shiftStatusKey, holderLabel } from './derive';
import { useSchedule } from './scheduleContext';

/**
 * What a coordinator cannot read off the block itself: whether this person is
 * free later in the day, whether they said they can come another day or expect
 * other duties, and everything they are already standing for across the event.
 *
 * The block stays the target unchanged — Mantine chains its own mouse handlers
 * onto the child's rather than replacing them, so the board's dim/highlight
 * still fires on the same hover. (That is only true outside `HoverCard.Group`:
 * in a group the target's props come from floating-ui and would overwrite
 * `onMouseEnter`, taking the highlight with them.)
 */
export function VolunteerHoverCard({
  volunteer,
  shift,
  data,
  disabled,
  children,
}: {
  volunteer: PoolVolunteer;
  shift: Shift;
  data: TimelinePayload;
  /** A drag is under way — the card would only be in the way. */
  disabled?: boolean;
  children: React.ReactElement;
}) {
  return (
    <HoverCard
      openDelay={250}
      closeDelay={150}
      offset={4}
      position="bottom-start"
      width={330}
      shadow="md"
      withArrow
      disabled={disabled}
    >
      <HoverCard.Target>{children}</HoverCard.Target>
      <HoverCard.Dropdown className="tl-hovercard">
        <CardBody volunteer={volunteer} shift={shift} data={data} />
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

/**
 * Its own component so the day grouping runs when the card is on screen rather
 * than on every board render — the dropdown is not mounted while closed.
 */
function CardBody({
  volunteer,
  shift,
  data,
}: {
  volunteer: PoolVolunteer;
  shift: Shift;
  data: TimelinePayload;
}) {
  const day = data.day;
  const rollup = useSchedule().get(volunteer.id);
  const external = isExternal(volunteer);
  // Only active groups travel with the board, so a team can be missing here
  // while its place is still on a shift — the badge follows the place, not the
  // lookup, or a team would wear a person's avatar.
  const team = external
    ? data.externalGroups.find((g) => g.id === volunteer.externalGroupId)
    : undefined;

  return (
    <>
      <div className="tl-hc-head">
        <span className={`tl-avatar${external ? ' tl-avatar--team' : ''}`}>
          {external ? '▪' : initials(volunteer.name)}
        </span>
        <span className="tl-hc-name">{holderLabel(volunteer)}</span>
        <span className={`tl-status-dot tl-status-dot--${shiftStatusKey(shift, volunteer)}`} />
      </div>

      {external ? (
        <>
          <div className="tl-hc-eyebrow">ZUNANJA EKIPA</div>
          <p className="tl-hc-team">
            {team ? team.leaderName || team.name : 'ekipa ni znana'}
            {team?.leaderPhone ? (
              <>
                {' · '}
                <span className="tl-hc-tel">{team.leaderPhone}</span>
              </>
            ) : null}
          </p>
        </>
      ) : (
        <>
          <div className="tl-hc-eyebrow">
            RAZPOLOŽLJIVOST · {day.label.toUpperCase()}
          </div>
          <Availability availability={volunteer.availability} dayKey={day.key} />
          <div className="tl-hc-flags">
            <Flag label="Več dni" value={volunteer.canMultiDay} />
            <Flag label="Druge naloge" value={volunteer.otherTasks} warnOnYes />
          </div>
        </>
      )}

      {rollup ? (
        <>
          <div className="tl-hc-eyebrow tl-hc-eyebrow--split">
            IZMENE <span className="tl-hc-total">skupaj {formatDuration(rollup.minutes)}</span>
          </div>
          <Days rollupShifts={rollup.shifts} day={day} currentShiftId={shift.id} />
        </>
      ) : (
        // The other days have not arrived yet; the viewed day's load is already
        // in the board payload, so the card says that much rather than nothing.
        <>
          <div className="tl-hc-eyebrow tl-hc-eyebrow--split">
            IZMENE <span className="tl-hc-total">ta dan {formatDuration(volunteer.dayMinutes)}</span>
          </div>
          <p className="tl-hc-pending">razpored za druge dni se še nalaga</p>
        </>
      )}
    </>
  );
}

function Days({
  rollupShifts,
  day,
  currentShiftId,
}: {
  rollupShifts: VolunteerShift[];
  day: EventDay;
  currentShiftId: string;
}) {
  const { setSelectedDay, selectShift, setAsideTab } = useUi();
  const groups = groupShiftsByDay(rollupShifts);

  return (
    <div className="tl-hc-days">
      {groups.map((g) => (
        <div
          key={g.eventDayId}
          className={`tl-hc-day${g.eventDayId === day.id ? ' tl-hc-day--now' : ''}`}
        >
          <div className="tl-hc-dayhead">
            <b>{g.dayLabel}</b>
            <span className="tl-hc-daysum">{formatDuration(g.minutes)}</span>
          </div>
          {g.shifts.map((s) => (
            <button
              key={s.id}
              type="button"
              className={[
                'tl-hc-slot',
                s.id === currentShiftId ? 'tl-hc-slot--current' : '',
                s.clash ? 'tl-hc-slot--clash' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              title={`${g.dayLabel} · ${s.postCode} ${range(s.fromMin, s.toMin)} — pokaži na časovnici`}
              onClick={() => {
                // Switching days also drops the sector focus, so a slot on the
                // day already on screen must not go through it.
                if (s.eventDayId !== day.id) setSelectedDay(s.eventDayId);
                selectShift(s.id);
                setAsideTab('podrobnosti');
              }}
            >
              <span className="tl-hc-slottop">
                <span className="tl-hc-slottime">{range(s.fromMin, s.toMin)}</span>
                <span className="tl-hc-slotlen">{formatDuration(s.toMin - s.fromMin)}</span>
              </span>
              <span className="tl-hc-slotplace">
                <span className="tl-hc-slotcode">{s.postCode}</span>
                {s.postMicro ? ` · ${s.postMicro}` : ''}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

const AVAIL_CLASS: Record<AvailabilityValue, string> = {
  'na-voljo': 'tl-hc-av--yes',
  delno: 'tl-hc-av--partial',
  'ni-na-voljo': 'tl-hc-av--no',
  neznano: 'tl-hc-av--unknown',
};
const AVAIL_MARK: Record<AvailabilityValue, string> = {
  'na-voljo': '✓',
  delno: '◐',
  'ni-na-voljo': '✕',
  neznano: '',
};

/** Every slot the signup sheet asked about for this day, in its own colour. */
function Availability({
  availability,
  dayKey,
}: {
  availability: Record<string, AvailabilityValue>;
  dayKey: string;
}) {
  const slots = AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey);
  if (slots.length === 0) return null;
  return (
    <div className="tl-hc-avail">
      {slots.map((slot) => {
        // A slot nobody answered is unknown, which is not the same as a "no".
        const value = availability[slot.id] ?? 'neznano';
        return (
          <span
            key={slot.id}
            className={`tl-hc-av ${AVAIL_CLASS[value]}`}
            title={`${slot.name} (${slot.hours}) — ${AVAILABILITY_VALUE_LABEL[value]}`}
          >
            {slot.name} <span className="tl-hc-avh">{slot.hours}</span>
            {AVAIL_MARK[value] ? <span className="tl-hc-avm">{AVAIL_MARK[value]}</span> : null}
          </span>
        );
      })}
    </div>
  );
}

function Flag({
  label,
  value,
  warnOnYes,
}: {
  label: string;
  value: boolean | null;
  /** "Druge naloge: da" is something to keep in mind, not something good. */
  warnOnYes?: boolean;
}) {
  const tone =
    value === null ? 'unknown' : value ? (warnOnYes ? 'flag' : 'yes') : 'no';
  return (
    <span className={`tl-hc-flag tl-hc-flag--${tone}`}>
      {label} <b>{value === null ? 'ni podatka' : value ? 'da' : 'ne'}</b>
    </span>
  );
}
